import AuthModule from '@repository/modules/auth';
import UserModule from '@repository/modules/user';
import ChatModule from '@repository/modules/chat';
import EventModule from '@repository/modules/event';
import {
    useErrorLogger,
    type PossibleCaptureExceptionFn,
} from '@composables/useErrorLogger';
import type { FetchResponse } from 'ofetch';

export type ApiInstances = {
    auth: AuthModule;
    user: UserModule;
    chat: ChatModule;
    event: EventModule;
};

export type DefaultCustomData = {
    method?: string;
};

export type ExtendedResponse<T = DefaultCustomData> = FetchResponse<any> & {
    customData: T;
};

const skipClearCookiesRedirectionPathRegexes = [
    /\/login/,
    /\/api\/translations/,
];

const handleUnauthorized = async (request: string) => {
    await $fetch('/api/clear-cookies');
    const skipNavigation = skipClearCookiesRedirectionPathRegexes.some(
        (regex) => regex.test(request)
    );
    if (skipNavigation) return;
    await navigateTo('/auth');
    window.location.reload();
};

const handleServerError = <
    T extends {} = { method?: string; statusCode?: number },
>(
    endpoint: string,
    extra: T,
    captureExceptionFn: PossibleCaptureExceptionFn
) => {
    captureExceptionFn(new FetchError(`Failed to fetch endpoint ${endpoint}`), {
        extra: {
            endpoint,
            ...extra,
        },
    });
};

export default defineNuxtPlugin(() => {
    const config = useRuntimeConfig();

    const { errorLoggerCaptureException } = useErrorLogger();

    const apiFetcher = $fetch.create({
        baseURL: config.public.baseUrl,
        onResponse({ response, options }) {
            (response as ExtendedResponse).customData = {
                method: options.method,
            };
        },
        async onResponseError({ request, response }) {
            const endpoint = new URL(request.toString()).pathname;
            if (response.status === 401) await handleUnauthorized(endpoint);
            if (response.status === 500) {
                handleServerError(
                    endpoint,
                    {
                        method: (response as ExtendedResponse).customData
                            .method,
                        statusCode: response.status,
                    },
                    errorLoggerCaptureException
                );
            }
        },
    });

    const authModule = new AuthModule(apiFetcher);
    const userModule = new UserModule(apiFetcher);
    const chatModule = new ChatModule(apiFetcher);
    const eventModule = new EventModule(apiFetcher);

    const modules: ApiInstances = {
        auth: authModule,
        user: userModule,
        chat: chatModule,
        event: eventModule,
    };

    return {
        provide: {
            api: modules,
        },
    };
});
